import { type ReactNode } from "react";
import { Link } from "@/lib/router-compat";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, Check, MapPin, PhoneCall } from "lucide-react";
import AgencyNavbar from "@/components/agency/AgencyNavbar";
import AgencyFooter from "@/components/agency/AgencyFooter";
import SEOHead from "@/components/SEOHead";
import { SITE_ORIGIN } from "@/lib/localSeo";
import localHero from "@/assets/agency/contractor-website-in-use.jpg";

export type Crumb = { name: string; path: string };
export type LinkItem = { label: string; to: string; note?: string };

type Props = {
  title: string;
  description: string;
  canonicalPath: string;
  eyebrow: string;
  h1: string;
  lead: string;
  bodyHeading: string;
  body: string;
  checklistTitle: string;
  checklist: string[];
  areasTitle?: string;
  areas?: string[];
  linkSections?: { title: string; intro?: string; links: LinkItem[] }[];
  faqs: { q: string; a: string }[];
  crumbs: Crumb[];
  serviceName: string;
  areaServed: string[];
  extra?: ReactNode;
  heroImage?: string;
  heroAlt?: string;
};

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5 },
};

const LocalLandingLayout = ({
  title,
  description,
  canonicalPath,
  eyebrow,
  h1,
  lead,
  bodyHeading,
  body,
  checklistTitle,
  checklist,
  areasTitle,
  areas,
  linkSections = [],
  faqs,
  crumbs,
  serviceName,
  areaServed,
  extra,
  heroImage,
  heroAlt,
}: Props) => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: serviceName,
        description,
        serviceType: "Contractor website design and local SEO",
        url: `${SITE_ORIGIN}${canonicalPath}`,
        provider: {
          "@type": "LocalBusiness",
          name: "jaik",
          url: SITE_ORIGIN,
          telephone: "+1-253-293-1550",
          address: { "@type": "PostalAddress", addressLocality: "Seattle", addressRegion: "WA", addressCountry: "US" },
          priceRange: "$400-$1,499",
        },
        areaServed: areaServed.map((name) => ({ "@type": "City", name })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: crumbs.map((c, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: c.name,
          item: `${SITE_ORIGIN}${c.path}`,
        })),
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-agency-bg font-sans text-agency-text">
      <SEOHead title={title} description={description} canonical={`${SITE_ORIGIN}${canonicalPath}`} jsonLd={jsonLd} />
      <AgencyNavbar />

      <main>
        <section className="relative min-h-[640px] sm:min-h-[680px] flex items-end overflow-hidden">
          <img src={heroImage ?? localHero} alt={heroAlt ?? "Contractor checking his website on a phone at the tailgate of a work truck"} width={1536} height={960} className="absolute inset-0 h-full w-full object-cover object-[68%_center]" />
          <div className="absolute inset-0 bg-gradient-to-r from-agency-bg via-agency-bg/90 to-agency-bg/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-agency-bg via-transparent to-agency-bg/35" />
          <div className="container relative z-10 pb-12 pt-32 sm:pb-16 sm:pt-40">
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex flex-wrap items-center gap-2 text-xs text-agency-text-muted/70">
                {crumbs.map((c, i) => (
                  <li key={c.path} className="flex items-center gap-2">
                    {i > 0 && <span aria-hidden="true">/</span>}
                    {i === crumbs.length - 1 ? (
                      <span className="text-agency-text-muted">{c.name}</span>
                    ) : (
                      <Link to={c.path} className="hover:text-agency-text transition-colors">
                        {c.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ol>
            </nav>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
              <span className="font-sans text-xs text-agency-accent tracking-[0.2em] uppercase mb-4 block">{eyebrow}</span>
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-5 [text-wrap:balance]">{h1}</h1>
              <p className="font-sans text-agency-text-muted text-lg leading-relaxed mb-8">{lead}</p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/book"
                  className="btn-jaik btn-jaik-primary"
                >
                  <Calendar className="h-4 w-4" /> Book a free call <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/pricing"
                  className="btn-jaik btn-jaik-secondary"
                >
                  See packages and pricing
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="pb-14 sm:pb-20">
          <div className="container grid lg:grid-cols-[1.2fr_1fr] gap-10">
            <motion.div {...fadeUp}>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-4">{bodyHeading}</h2>
              <p className="font-sans text-agency-text-muted leading-relaxed">{body}</p>
              {extra}
            </motion.div>

            <motion.div {...fadeUp} className="bg-agency-surface border border-agency-border rounded-2xl p-6 sm:p-7 h-fit">
              <h2 className="font-heading text-lg font-semibold mb-4">{checklistTitle}</h2>
              <ul className="space-y-3">
                {checklist.map((c) => (
                  <li key={c} className="flex items-start gap-2.5 text-sm text-agency-text leading-relaxed">
                    <Check className="h-4 w-4 text-agency-accent mt-0.5 shrink-0" strokeWidth={1.75} />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </section>

        {areas && areas.length > 0 && (
          <section className="pb-14 sm:pb-20">
            <div className="container">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-5">{areasTitle}</h2>
              <ul className="flex flex-wrap gap-2.5">
                {areas.map((a) => (
                  <li
                    key={a}
                    className="inline-flex items-center gap-1.5 rounded-full border border-agency-border bg-agency-surface/60 px-4 py-2 text-sm text-agency-text-muted"
                  >
                    <MapPin className="h-3.5 w-3.5 text-agency-accent" /> {a}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {linkSections.map((section) => (
          <section key={section.title} className="pb-14 sm:pb-20">
            <div className="container">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">{section.title}</h2>
              {section.intro && <p className="font-sans text-agency-text-muted mb-6 max-w-2xl">{section.intro}</p>}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {section.links.slice(0, 6).map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="group bg-agency-surface/60 border border-agency-border rounded-xl px-5 py-4 hover:border-agency-accent/40 transition-colors"
                  >
                    <span className="font-sans text-sm font-medium text-agency-text flex items-center justify-between gap-3">
                      {l.label}
                      <ArrowRight className="h-4 w-4 text-agency-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                    {l.note && <span className="block font-sans text-xs text-agency-text-muted mt-1.5 leading-relaxed">{l.note}</span>}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        ))}

        <section className="pb-14 sm:pb-20">
          <div className="container max-w-3xl">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-6">Questions contractors ask</h2>
            <div className="space-y-4">
              {faqs.map((f) => (
                <div key={f.q} className="bg-agency-surface/60 border border-agency-border rounded-xl p-5">
                  <h3 className="font-heading text-base font-semibold mb-2">{f.q}</h3>
                  <p className="font-sans text-sm text-agency-text-muted leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-24 bg-agency-surface/30">
          <div className="container text-center">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-5">
              Want to see it before you pay for it?
            </h2>
            <p className="font-sans text-agency-text-muted text-lg mb-8 max-w-xl mx-auto">
              Book a 20 minute call and I will build you a free mockup of your homepage. No contract, no pressure.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/book"
                className="btn-jaik btn-jaik-primary btn-jaik-lg"
              >
                <Calendar className="h-4 w-4" /> Book a call, get a free mockup
              </Link>
              <a
                href="tel:+12532931550"
                className="btn-jaik btn-jaik-secondary btn-jaik-lg"
              >
                <PhoneCall className="h-4 w-4" /> (253) 293-1550
              </a>
            </div>
          </div>
        </section>
      </main>

      <AgencyFooter />
    </div>
  );
};

export default LocalLandingLayout;
